import React, { use, useCallback, useEffect, useEffectEvent, useState } from 'react'
import { Navigate, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useMutation } from '@apollo/client'
import { css } from '@emotion/core'
import { array, bool, func, node, number, oneOfType, string } from 'prop-types'

import { useCanary } from '@bill/canary.react'
import useDidMount from '@divvy-web/hooks.usedidmount'
import { FormattedMessage } from '@divvy-web/i18n'
import { FormContext } from '@divvy-web/skylab.form'
import Spinner from '@divvy-web/skylab.spinner'
import { TOAST_TYPE_DANGER, TOAST_TYPE_SUCCESS, useToast } from '@divvy-web/skylab.toast'
import { curry, pickBy } from '@divvy-web/utils'

import { getTokenFromStorage, useAuth } from '@sputnik/auth'
import { getCreditAppIdFromToken, getTypeFromToken } from '@sputnik/auth/jwt'
import { FormButtons } from '@sputnik/components'
import InviteCollaboratorsLink from '@sputnik/components/InviteCollaboratorsLink'
import PageHeading from '@sputnik/components/PageHeading/PageHeading'
import PageViewWrapper from '@sputnik/components/PageViewWrapper/PageViewWrapper'
import PhoneVerificationModalContainer from '@sputnik/components/PhoneVerificationModalContainer'
import TimeoutWarningModal from '@sputnik/components/TimeoutWarningModal'
import { isPhoneNumberVerified } from '@sputnik/components/utils'
import useRemainingCorrections from '@sputnik/hooks/useRemainingCorrections'
import { initialValues } from '@sputnik/pages/FormPages/initialValues'
import UpdateSavedSections from '@sputnik/pages/gql/mutations/UpdateSavedSections.gql'
import {
  ADVANCED_ONBOARDING,
  AUTHORIZED_SIGNER,
  BENEFICIAL_OWNER_0,
  BENEFICIAL_OWNER_1,
  BENEFICIAL_OWNER_2,
  BENEFICIAL_OWNER_3,
  CREDIT_APPLICATION,
  CreditApplicationStatus,
  EDITABLE_STATUSES,
  LEGAL_BUSINESS_NAME,
  MAJOR_ACCOUNT_CHANGE,
  PAGE_NAME_STRINGS,
  PATHNAME_CONSTS,
  TAX_ID,
} from '@sputnik/resources/constants'
import { useWebTracking } from '@sputnik/tracking/WebTrackingContext'
import { getBillUrl } from '@sputnik/utils/billUtils'
import { matchEmail } from '@sputnik/utils/dataUtils'
import { logInfo, logWarning } from '@sputnik/utils/loggerUtils'
import { getPageMutationQuery, getPageMutationVariables, handleMutationError } from '@sputnik/utils/mutationUtils'
import { getReturnFlowLocalStorage } from '@sputnik/utils/neoRedirectUtils'

import IncompleteErrorModal from './IncompleteErrorModal'
import IncompleteInfoBanner from './IncompleteInfoBanner'

const getSectionHasSaved = (formValues, pageIndex) => {
  const savedSections = formValues?.savedSections || formValues?._savedSections

  switch (pageIndex) {
    case 0:
      return savedSections?.includes('BUSINESS_INFO')
    case 1:
      return savedSections?.includes('FINANCE_INFO')
    case 2:
      return savedSections?.includes('AUTH_SIGNER')
    case 3:
      return savedSections?.includes('COMPANY_OWNERS')
  }
}

const FormPageWrapper = ({
  isMigration,
  mutationKey,
  pageSubTitle,
  setIsSaving,
  bdcOrgId,
  children,
  clickedSaveAndExit,
  hidePrevious,
  pageCount,
  pageIndex,
  pageName,
  pageTitle,
  pages,
  refetchFormValues,
  setClickedSaveAndExit,
  setShouldShowIncompleteBanner,
  shouldShowIncompleteBanner,
}) => {
  const navigate = useNavigate()
  const showToast = useToast()
  const showMfaLogic = useCanary('show-mfa-logic')
  const { email: loginEmail, requestApplicantToken } = useAuth()
  const { appId } = useParams()
  const location = useLocation()
  const { pathname, search, state } = location
  const { track } = useWebTracking()
  const isUpdateMode = !!state?.isUpdateMode
  const { formHasChanged, formIsValid, formValues, getFormValue, validationErrors } = use(FormContext)
  const searchParams = search ?? ''
  const { email, majorityHolders, status, creditReportConsent } = formValues
  const requestingCorrection = status === CreditApplicationStatus.CORRECTION_REQUESTED
  const { completedCorrections, remainingCorrections, removeFromRemainingCorrections } = useRemainingCorrections(
    appId,
    getFormValue('sectionsToCorrect'),
    requestingCorrection,
  )
  const { PAGE_REVIEW_AND_SIGN, PAGE_COMPANY_OWNERS, PAGE_BUSINESS_INFO, PAGE_FINANCE_INFO, PAGE_AUTHORIZED_SIGNER } =
    PAGE_NAME_STRINGS
  const isReviewAndSignPage = pageName === PAGE_REVIEW_AND_SIGN
  const isAuthSignerPage = pageName === PAGE_AUTHORIZED_SIGNER
  const isCompanyOwnersPage = pageName === PAGE_COMPANY_OWNERS
  const isBusinessInfoPage = pageName === PAGE_BUSINESS_INFO
  const hasEditableStatus = !status || EDITABLE_STATUSES.includes(status)
  const isReviewEdit = isReviewAndSignPage && !!getFormValue('_isReviewEdit')
  const isFirstPage = pageIndex === 0

  // for state-based disclosures

  const loggedInUserIsAuthorizedSigner = matchEmail(email, loginEmail)
  const nextRoute = pages[pageIndex + 1]?.route
  const previousRoute = pages[Math.max(pageIndex - 1, 0)]?.route
  const [currentSearchParams, setSearchParams] = useSearchParams()
  const editingSubPage = !!currentSearchParams.get('owner')
  const [navigatingToNextRoute, setNavigatingToNextRoute] = useState(null)
  const onNavigateToNextRouteUpdate = useEffectEvent((newRoute) => {
    setNavigatingToNextRoute(newRoute)
  })
  const [pageLoadedOnce, setPageLoadedOnce] = useState(() => false)
  const [exitBtnClickedOnce, setExitBtnClickedOnce] = useState(false)
  const [nextBtnClickedOnce, setNextBtnClickedOnce] = useState(false)
  const [showIncompleteModal, setShowIncompleteModal] = useState(false)
  const submissionPendingUrl = window.sessionStorage.getItem('submissionPendingUrl')
  const isMajorAccountChange = getFormValue('recordType') === MAJOR_ACCOUNT_CHANGE
  const isAdvancedOnboardingReturnEnabled = useCanary('advanced-onboarding-return-flow')
  // Keep track of major account changes and what has been updated to determine when to enable the submit button
  const majorAccountChanges = getFormValue('_majorAccountChanges')
  const hasBusinessInfoMajorAccountChange =
    majorAccountChanges?.includes(LEGAL_BUSINESS_NAME) || majorAccountChanges?.includes(TAX_ID)
  const hasAuthorizedSignerMajorAccountChange = majorAccountChanges?.includes(AUTHORIZED_SIGNER)
  const hasCompanyOwnersMajorAccountChange =
    majorAccountChanges?.includes(BENEFICIAL_OWNER_0) ||
    majorAccountChanges?.includes(BENEFICIAL_OWNER_1) ||
    majorAccountChanges?.includes(BENEFICIAL_OWNER_2) ||
    majorAccountChanges?.includes(BENEFICIAL_OWNER_3)
  const hasPersonMajorAccountChange = hasAuthorizedSignerMajorAccountChange || hasCompanyOwnersMajorAccountChange

  const isBusinessInfoMacCompleted = hasBusinessInfoMajorAccountChange
    ? getFormValue('_savedSections')?.includes('BUSINESS_INFO')
    : true
  const isAuthorizedSignerMacCompleted = hasAuthorizedSignerMajorAccountChange
    ? getFormValue('_savedSections')?.includes('AUTH_SIGNER')
    : true
  const isCompanyOwnersMacCompleted = hasPersonMajorAccountChange
    ? getFormValue('_savedSections')?.includes('COMPANY_OWNERS')
    : true

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [pageName])

  // Data coming back from mutations can update form values resulting in a rerender which disables execution of navigate
  // This effect ensures navigation after all other cache/state updates
  useEffect(() => {
    if (navigatingToNextRoute) {
      onNavigateToNextRouteUpdate(null)
      if (isUpdateMode) {
        showToast(
          TOAST_TYPE_SUCCESS,
          <FormattedMessage
            defaultMessage='Successfully updated {title}.'
            id='sputnik.FormPageWrapper__6zkkYj'
            values={{
              title: pageName === PAGE_FINANCE_INFO ? 'Financial Information' : 'Company Owners',
            }}
          />,
          {
            autoHideDelay: 5000,
            dataTestId: 'section-submission-success',
          },
        )
      }
      navigate(navigatingToNextRoute)
    }
  }, [PAGE_FINANCE_INFO, isUpdateMode, navigate, navigatingToNextRoute, pageName, showToast])

  useEffect(() => {
    if (submissionPendingUrl && submissionPendingUrl.includes(appId)) {
      navigate({ pathname: submissionPendingUrl })
    }
  }, [appId, hasEditableStatus, navigate, search, submissionPendingUrl])

  useEffect(() => {
    if (!hasEditableStatus && !pageLoadedOnce && (!submissionPendingUrl || !submissionPendingUrl.includes(appId))) {
      logWarning('creditAppRedirect', {
        attributes: {
          action: 'redirect',
          creditAppId: appId,
          status: status,
        },
        eventName: 'PageLoad',
        message: `Redirecting to the dashboard since the credit application status is ${status}`,
      })

      navigate({ pathname: PATHNAME_CONSTS.DASHBOARD_PATH, search }, { state })
    }
  }, [hasEditableStatus, navigate, state, search, appId, status, pageLoadedOnce, submissionPendingUrl])

  // After all useEffects run, we can set the pageLoadedOnce to true.
  useDidMount(() => {
    setPageLoadedOnce(true)
  })

  const maybeRerequestApplicantToken = async () => {
    const token = getTokenFromStorage()
    if (getTypeFromToken(token) !== 'applicant_token' || getCreditAppIdFromToken(token) !== appId) {
      await requestApplicantToken(appId, true)
    }
  }

  const getNextButtonText = () => {
    if (isUpdateMode)
      return (
        <FormattedMessage
          defaultMessage='Update'
          id='sputnik.FormPageWrapper__BWpuKl'
        />
      )
    if (!isReviewAndSignPage) return null
    if (loggedInUserIsAuthorizedSigner)
      return (
        <FormattedMessage
          defaultMessage='Submit'
          id='sputnik.FormPageWrapper__wSZR47'
        />
      )
    return (
      <FormattedMessage
        defaultMessage='Send to signer'
        id='sputnik.FormPageWrapper__shdH4T'
      />
    )
  }

  const getIncompleteBannerText = () => {
    if (isUpdateMode) return 'update'
    if (!isReviewAndSignPage) return 'next'
    if (loggedInUserIsAuthorizedSigner) return 'submit'
    return 'send to signer'
  }

  const gqlMutation = getPageMutationQuery(mutationKey, loggedInUserIsAuthorizedSigner, isMajorAccountChange)
  const gqlValues = isReviewAndSignPage ? initialValues({}, CREDIT_APPLICATION) : initialValues({}, pageName)

  Object.keys(gqlValues).forEach((key) => (gqlValues[key] = formValues?.[key]))

  const gqlPageValues = { [mutationKey]: gqlValues }
  const gqlVariables = getPageMutationVariables(mutationKey, gqlPageValues, appId, loggedInUserIsAuthorizedSigner)
  const [nextPath, setNextPath] = useState('')

  const filledValues = pickBy((v) => !!v && v !== '+1', gqlValues)
  const formatErrors = pickBy((_, k) => Object.keys(filledValues).includes(k), validationErrors)
  const hasFormatErrors = formatErrors && !!Object.keys(formatErrors).length

  const correctionSection = pages[pageIndex].correctionSection
  const correctionRequired = correctionSection && remainingCorrections.includes(correctionSection)
  const correctionCompleted = correctionSection && completedCorrections.includes(correctionSection)
  const correctionRequestedForOtherSections =
    !isReviewAndSignPage && requestingCorrection && !correctionRequired && !correctionCompleted
  const isBeingSigned = isReviewAndSignPage && loggedInUserIsAuthorizedSigner

  const shouldShowMfaModal =
    showMfaLogic &&
    !getFormValue('_isReviewEdit') &&
    ((isAuthSignerPage && loggedInUserIsAuthorizedSigner) || isBeingSigned) &&
    !isMajorAccountChange &&
    !isMigration &&
    !isPhoneNumberVerified(getFormValue)

  const showSuccessToast = useToast(TOAST_TYPE_SUCCESS)
  const handleReviewAndSignSubmission = useCallback(() => {
    navigate({
      pathname: `/app/${appId}/section/${PATHNAME_CONSTS.SUBMITTING_APPLICATION}`,
    })
  }, [navigate, appId])

  const getNewNextPath = useCallback(() => {
    const processApplication = isReviewAndSignPage && !isMigration && loggedInUserIsAuthorizedSigner
    if (isUpdateMode) return `/app/${appId}/section/review-and-sign`

    if (processApplication) {
      return `/app/${appId}/section/${PATHNAME_CONSTS.SUBMITTING_APPLICATION}`
    }
    if (isReviewAndSignPage) return `/app/${appId}/section/success`
    return `/app/${appId}/section/${nextRoute}`
  }, [appId, isMigration, isReviewAndSignPage, isUpdateMode, loggedInUserIsAuthorizedSigner, nextRoute])

  const onMutationCompleted = () => {
    if (requestingCorrection && correctionRequired) {
      removeFromRemainingCorrections(correctionSection)
    }
    const newNextPath = nextPath && nextPath !== pathname ? nextPath : getNewNextPath()
    let result, eventName

    if (isBeingSigned) {
      result = 'Successfully submitted the credit app'
      eventName = 'SignCreditApp'
    } else if (isReviewAndSignPage && !loggedInUserIsAuthorizedSigner) {
      result = 'Successfully sent the credit app to signer'
      eventName = 'SendToSigner'
    } else {
      result = `Sending user to path: ${newNextPath}`
      eventName = 'PageSave'
    }

    logInfo('creditAppSaveSuccess', {
      attributes: {
        desiredCreditFix: true,
        creditAppId: appId,
        result,
      },
      eventName: eventName,
    })

    if (isBeingSigned) {
      track('se_signup__application_v2_review_and_sign__submitted')
      showSuccessToast(
        <FormattedMessage
          defaultMessage='Application submitted'
          id='sputnik.FormPageWrapper__HHjuhD'
        />,
        {
          autoHideDelay: 5000,
        },
      )
      handleReviewAndSignSubmission()
    } else {
      setNavigatingToNextRoute({ pathname: newNextPath, search: searchParams })
    }
  }

  const onMutationError = (error) => {
    const action = isReviewAndSignPage
      ? loggedInUserIsAuthorizedSigner
        ? 'SignCreditApp'
        : 'SendToSigner'
      : `${pageName}Update`
    const eventName = isReviewAndSignPage ? 'ReviewAndSignSubmit' : 'PageSave'

    handleMutationError({
      action,
      appId,
      error,
      eventName,
      navigateToDashboard,
      refetchFormValues,
      showErrorToast,
    })
  }

  const [updatePageMutation, { loading }] = useMutation(gqlMutation, {
    ...gqlVariables,
    onCompleted: onMutationCompleted,
    onError: onMutationError,
  })

  const shouldRouteToBdc =
    getReturnFlowLocalStorage() === ADVANCED_ONBOARDING && bdcOrgId && isAdvancedOnboardingReturnEnabled
  const billBaseUrl = getBillUrl(bdcOrgId)
  const goToBill = () => {
    logInfo('goToBill', {
      attributes: {
        creditAppId: appId,
        result: `Sending user to path: ${billBaseUrl}/neo/frame/setup`,
      },
      eventName: 'RedirectToBill',
    })
    window.location.href = `${billBaseUrl}/neo/frame/setup`
  }

  const [updateSavedSections, { loading: sectionsUpdateLoading }] = useMutation(UpdateSavedSections, {
    onCompleted: onMutationCompleted,
    onError: onMutationError,
    variables: { creditApplicationId: appId, step: 4 },
  })

  const showErrorToast = useCallback(
    (msg) =>
      showToast(TOAST_TYPE_DANGER, msg, {
        autoHideDelay: 5000,
        dataTestId: 'page-submission-fail',
      }),
    [showToast],
  )

  const navigateToDashboard = useCallback(
    () => navigate({ pathname: PATHNAME_CONSTS.DASHBOARD_PATH, search }, { state }),
    [state, search, navigate],
  )

  const handleSaveAndExit = () => {
    if (shouldShowMfaModal && !isReviewAndSignPage) {
      setSearchParams({ ...currentSearchParams, phoneVerificationRequired: true, actionSaveAndExit: true })
    } else {
      savePage()
    }
  }

  const savePage = async () => {
    logInfo('creditAppExit', {
      attributes: {
        creditAppId: appId,
        destination: PATHNAME_CONSTS.DASHBOARD_PATH,
      },
      eventName: 'NavigationButtonClick',
      message: 'Sending user to path: /dashboard',
    })
    if (shouldRouteToBdc) {
      goToBill()
    } else if (isReviewAndSignPage) {
      navigate({ pathname: PATHNAME_CONSTS.DASHBOARD_PATH, search: searchParams })
    } else if (isCompanyOwnersPage && majorityHolders === 'yes') {
      setNextPath(PATHNAME_CONSTS.DASHBOARD_PATH)
      updateSavedSections()
    } else {
      await maybeRerequestApplicantToken()
      setNextPath(PATHNAME_CONSTS.DASHBOARD_PATH)
      updatePageMutation({
        variables: isCompanyOwnersPage
          ? { beneficialOwners: [], hasBeneficialOwner: false }
          : { target: 'SAVE_AND_EXIT' },
      })
    }
  }

  useEffect(() => {
    if (!clickedSaveAndExit && formIsValid) setShouldShowIncompleteBanner(false)
    if (clickedSaveAndExit && !hasFormatErrors) setShouldShowIncompleteBanner(false)
  }, [clickedSaveAndExit, formIsValid, hasFormatErrors, setShouldShowIncompleteBanner])

  useEffect(() => {
    // warn the user that they are about to leave the application without saving
    const handleBeforeUnload = (event) => {
      event.preventDefault()
      return (event.returnValue = '')
    }
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [])

  const handleInvalidNextClick = () => {
    setClickedSaveAndExit(false)
    setShouldShowIncompleteBanner(true)
    if (!nextBtnClickedOnce) {
      setNextBtnClickedOnce(true)
    } else {
      setShowIncompleteModal(true)
    }
  }

  const handleInvalidSaveClick = () => {
    setClickedSaveAndExit(true)
    setShouldShowIncompleteBanner(true)
    if (!exitBtnClickedOnce) {
      setExitBtnClickedOnce(true)
    } else {
      setShowIncompleteModal(true)
    }
  }

  const handleNextClick = () => {
    if (shouldShowMfaModal) {
      setSearchParams({ ...currentSearchParams, phoneVerificationRequired: 'true' })
    } else {
      submitPage()
    }
  }

  const submitPage = async () => {
    const newNextPath = getNewNextPath()
    setNextPath(newNextPath)

    logInfo('creditAppNextPage', {
      attributes: {
        creditAppId: appId,
        destination: newNextPath,
      },
      eventName: 'NavigationButtonClick',
    })

    await maybeRerequestApplicantToken()

    if (isCompanyOwnersPage) {
      if (majorityHolders === 'yes') {
        // Mutations are handled by the subpage if there are company owners
        updateSavedSections().then(() => setNavigatingToNextRoute({ pathname: newNextPath, search: searchParams }))
      } else {
        updatePageMutation({
          onCompleted: onMutationCompleted,
          onError: onMutationError,
          variables: { beneficialOwners: [], hasBeneficialOwner: false },
        })
      }
      return
    }

    updatePageMutation()
    setNextBtnClickedOnce(false)
    setExitBtnClickedOnce(false)
  }

  const handlePreviousClick = () => {
    const previousPath = isFirstPage ? PATHNAME_CONSTS.DASHBOARD_PATH : `/app/${appId}/section/${previousRoute}`

    logInfo('creditAppPreviousPage', {
      attributes: {
        creditAppId: appId,
        destination: previousPath,
      },
      eventName: 'NavigationButtonClick',
    })

    navigate({ pathname: previousPath, search: searchParams })
    setNextBtnClickedOnce(false)
    setExitBtnClickedOnce(false)
  }

  useDidMount(() => {
    logInfo('creditAppLoaded', {
      attributes: {
        creditAppId: appId,
        pageName,
      },
      eventName: 'PageLoad',
    })

    if (correctionRequestedForOtherSections) {
      logInfo('creditApplicationRedirect', {
        attributes: {
          creditAppId: appId,
          pageName,
        },
        eventName: 'PageRedirect',
        message: `${pageName} redirecting to reviewAndSign since no corrections needed`,
      })

      navigate({ pathname: `/app/${appId}/section/review-and-sign`, search: searchParams })
    }
  })

  useEffect(() => {
    setIsSaving(loading || sectionsUpdateLoading)
  }, [loading, sectionsUpdateLoading, setIsSaving])

  const pageSubTitleView = isBusinessInfoPage ? pageSubTitle(getFormValue('legalBusinessName')) : pageSubTitle
  const dsaVersion = getFormValue('dsaVersion')

  if (
    !isReviewAndSignPage &&
    formIsValid &&
    !formHasChanged &&
    getSectionHasSaved(formValues, pageIndex) &&
    state?.autoRedirectIndex === pageIndex
  ) {
    const path = getNewNextPath()
    return (
      <Navigate
        replace
        state={{ ...state, autoRedirectIndex: state?.autoRedirectIndex + 1 }}
        to={path}
      />
    )
  }

  if (!hasEditableStatus && !pageLoadedOnce) return <Spinner fullPage />

  const disableSubmitBtnForMac =
    isReviewAndSignPage &&
    isMajorAccountChange &&
    (!isBusinessInfoMacCompleted || !isAuthorizedSignerMacCompleted || !isCompanyOwnersMacCompleted)

  return (
    <PageViewWrapper
      inviteCollaborators
      dsaVersion={dsaVersion}
      pageName={pageName}
    >
      <section css={(theme) => formPageStyles({ isReviewAndSignPage }, theme)}>
        <div className='header-wrapper'>
          <PageHeading
            isFormPage
            dsaVersion={dsaVersion}
            page={pageName}
            pageCount={pageCount}
            pageIndex={pageIndex + 1}
            pageSubTitle={pageSubTitleView}
            pageTitle={pageTitle}
          />
        </div>
        <div className='content-wrapper'>
          {shouldShowIncompleteBanner && (
            <IncompleteInfoBanner
              buttonOption={getIncompleteBannerText()}
              isReviewAndSignPage={isReviewAndSignPage}
              isSaving={clickedSaveAndExit}
            />
          )}
          {children}
          <TimeoutWarningModal pageName={mutationKey} />
          <IncompleteErrorModal
            setShowModal={setShowIncompleteModal}
            showModal={showIncompleteModal}
          />
        </div>
        <div className='signed-in-info'>
          <div className='invite-collaborators'>
            <div className='invite-collaborators-link-web'>
              <InviteCollaboratorsLink />
            </div>
          </div>
          <div className='signed-as'>
            <FormattedMessage
              defaultMessage='You are signed in as {loginEmail}'
              id='sputnik.FormPageWrapper__HbqUP7'
              values={{ loginEmail }}
            />
          </div>
        </div>
      </section>

      <FormButtons
        disableExit={loading || editingSubPage || isReviewEdit || sectionsUpdateLoading}
        disableNext={
          editingSubPage ||
          isReviewEdit ||
          loading ||
          sectionsUpdateLoading ||
          disableSubmitBtnForMac ||
          !!(isReviewAndSignPage && loggedInUserIsAuthorizedSigner && !creditReportConsent)
        }
        disablePrevious={loading || editingSubPage || sectionsUpdateLoading}
        disableSaveOnInvalid={false}
        exitButtonText={
          isReviewAndSignPage ? (
            <FormattedMessage
              defaultMessage='Exit'
              id='sputnik.FormPageWrapper__E2sGaF'
            />
          ) : null
        }
        handleExitClick={!isReviewAndSignPage && hasFormatErrors ? handleInvalidSaveClick : handleSaveAndExit}
        handleNextClick={formIsValid ? handleNextClick : handleInvalidNextClick}
        handlePreviousClick={handlePreviousClick}
        hidePrevious={hidePrevious || isUpdateMode}
        isUpdateMode={isUpdateMode}
        nextButtonText={getNextButtonText()}
        pageName={pageName}
        showExitSpinner={(loading || sectionsUpdateLoading) && nextPath === PATHNAME_CONSTS.DASHBOARD_PATH}
        showNextSpinner={(loading || sectionsUpdateLoading) && nextPath !== PATHNAME_CONSTS.DASHBOARD_PATH}
      />
      {shouldShowMfaModal && (
        <PhoneVerificationModalContainer
          submitPageMutation={currentSearchParams.get('actionSaveAndExit') ? savePage : submitPage}
        />
      )}
    </PageViewWrapper>
  )
}

FormPageWrapper.propTypes = {
  bdcOrgId: string,
  children: node.isRequired,
  clickedSaveAndExit: bool.isRequired,
  hidePrevious: bool.isRequired,
  isMigration: bool,
  mutationKey: string,
  pageCount: number.isRequired,
  pageIndex: number.isRequired,
  pageName: string.isRequired,
  pageSubTitle: oneOfType([node, func]),
  pageTitle: node.isRequired,
  pages: array.isRequired,
  refetchFormValues: func.isRequired,
  setClickedSaveAndExit: func.isRequired,
  setIsSaving: func,
  setShouldShowIncompleteBanner: func.isRequired,
  shouldShowIncompleteBanner: bool.isRequired,
}

const formPageStyles = curry(
  ({ isReviewAndSignPage }, { mq, type }) => css`
    padding-bottom: var(--tri-space-1100);

    .content-wrapper {
      padding: 0 var(--tri-space-600) var(--tri-space-500) var(--tri-space-1500);
      ${mq.xSmallMaxWidth({
        marginTop: isReviewAndSignPage ? 'var(--tri-space-1500)' : 'var(--tri-space-300)',
        padding: '0 var(--tri-space-400) var(--tri-space-500) var(--tri-space-300)',
      })}
    }

    .signed-in-info {
      ${mq.smallMinWidth({ display: 'none' })}
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      gap: var(--tri-space-100);
      align-items: center;
      padding: 0 var(--tri-space-400) var(--tri-space-600) var(--tri-space-300);
    }

    .signed-as {
      text-align: center;
      ${type.triFontDesktopBodySmall};
    }
  `,
)

export default FormPageWrapper
